import { Box, Card, Container, Group, Table, Tabs, Title } from "@mantine/core";
import {
  type Fixture,
  type FixtureEvent,
  type League,
  type Lineup,
  type PlayerFixtureStatistics,
  type Team,
} from "@prisma/client";
import { type LoaderFunctionArgs } from "@remix-run/node";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  typedjson,
  type TypedMetaFunction,
  useTypedLoaderData,
} from "remix-typedjson";
import { PlayerFixturesList } from "#app/components/player-fixtures-list.tsx";
import { PlayerHeader } from "#app/components/player-header.tsx";
import { PlayerStatsLeaguesTable } from "#app/components/player-stats-leagues-table.tsx";
import { getHints } from "#app/utils/client-hints.tsx";
import { formatPlayerName, getCurrentSquad } from "#app/utils/players.ts";
import { getPlayer, getPlayerFixtures } from "../players.server";

dayjs.extend(utc);
dayjs.extend(timezone);

export type PlayerDetailFixtureResult = Fixture & {
  PlayerFixtureStatistics: Array<PlayerFixtureStatistics>;
  League: League;
  HomeTeam: Team;
  AwayTeam: Team;
  Lineup: Array<Lineup>;
  FixtureEvent: Array<FixtureEvent>;
};

// @ts-ignore - for some reason this is messing up with the deprecated type
export const meta: TypedMetaFunction<typeof loader> = ({ data }) => {
  if (data.status === "error") {
    return [{ title: "Elevens | Error" }];
  }
  return [{ title: `Elevens | ${formatPlayerName(data.player)}` }];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const hints = getHints(request);
  const { timeZone } = hints;

  // Get player ID
  const { id: playerIdParam } = params;
  if (!playerIdParam) {
    return typedjson({
      status: "error",
      message: "Missing player id",
    } as const);
  }
  const playerId = Number(playerIdParam);

  const todayTz = dayjs.tz(new Date(), timeZone).toDate();

  const player = await getPlayer(playerId, todayTz);

  if (!player) {
    return typedjson({
      status: "error",
      message: "Player not found",
    } as const);
  }

  const formattedPlayer = {
    ...player,
    PlayerStatistics: player.PlayerStatistics.map((stat) => ({
      ...stat,
      games_rating: Number(stat.games_rating || 0),
    })),
  };

  const fixtures = await getPlayerFixtures(player.id, {
    to: todayTz,
  });

  // Sort descending by date
  fixtures.sort((a, b) => b.date.getTime() - a.date.getTime());

  return typedjson({
    status: "idle",
    player: formattedPlayer,
    fixtures,
  } as const);
}

export default function Index() {
  const loaderData = useTypedLoaderData<typeof loader>();

  if (loaderData.status === "error") {
    return <div>{loaderData.message}</div>;
  }

  const { player, fixtures } = loaderData;

  const squadPlayer = getCurrentSquad(player);
  if (!squadPlayer) {
    return <div>Player not found</div>;
  }

  return (
    <Container size="xl" p={0}>
      <Card shadow="sm">
        <Group justify="space-between">
          <PlayerHeader
            player={player}
            position={squadPlayer.position}
            size="lg"
          />
          <Box>
            <Table withTableBorder>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th w={160}>Age</Table.Th>
                  <Table.Td>{player.age}</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Nationality</Table.Th>
                  <Table.Td>{player.nationality}</Table.Td>
                </Table.Tr>

                <Table.Tr>
                  <Table.Th>Birth Country</Table.Th>
                  <Table.Td>{player.birth_country}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Box>
        </Group>
      </Card>

      <Tabs defaultValue="matches">
        <Tabs.List>
          <Tabs.Tab value="matches">Matches</Tabs.Tab>
          <Tabs.Tab value="stats">Stats</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="matches">
          <PlayerFixturesList fixtures={fixtures} player={player} />
        </Tabs.Panel>

        <Tabs.Panel value="stats">
          <div>
            <Title order={3} mt="xl">
              Player stats by competition
            </Title>
            <PlayerStatsLeaguesTable player={player} />
          </div>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
